/**
 * Management words for backend values. Presentation only: statuses and codes come from the backend; this
 * file decides how a manager reads them. Raw backend messages are never shown.
 */
import { isApiError } from '@/api/errors';
import { formatLocation, isLocationType, venueVocabulary } from '@/venue/venue';
import type { ModifierGroup } from './api';

export function locationText(type: string, label: string): string {
  return isLocationType(type) ? formatLocation(type, label) : label;
}

/** "Table"/"Seat", or "Location" for a type this app does not know. */
export function locationNoun(type: string): string {
  return isLocationType(type) ? venueVocabulary(type).locationNoun : 'Location';
}

/** `VACANT`, `OCCUPIED`, `NEEDS_CLEANING` in a manager's words. An unknown status is shown readably. */
export function locationStatusLabel(status: string): string {
  switch (status) {
    case 'VACANT':
      return 'Vacant';
    case 'OCCUPIED':
      return 'In use';
    case 'NEEDS_CLEANING':
      return 'Needs cleaning';
    default: {
      const text = status.replace(/_/g, ' ').toLowerCase();
      return text.charAt(0).toUpperCase() + text.slice(1);
    }
  }
}

export function formatDuration(minutes: number): string {
  if (minutes < 1) return 'Just opened';
  if (minutes < 60) return `${Math.floor(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.floor(minutes % 60);
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

const HELP: Record<string, string> = {
  CALL_WAITER: 'Needs a waiter',
  WATER: 'Water',
  CUTLERY: 'Cutlery',
  BILL: 'Wants the bill',
  OTHER: 'Other request',
};

/** The neutral chip for help whose kind is not known: a request type this app has no words for. */
export const HELP_REQUESTED = 'Help requested';

/** A request type in the same words the waiter, the kitchen and the counter use. Never a guess. */
export function helpLabel(type: string): string {
  return HELP[type] ?? HELP_REQUESTED;
}

/** "1 choice group · Spice level", "2 choice groups · Spice level, Extras". */
export function choicesSummary(groups: { name: string }[]): string {
  const count = groups.length === 1 ? '1 choice group' : `${groups.length} choice groups`;
  return `${count} · ${groups.map((g) => g.name).join(', ')}`;
}

/** The choice rule of a modifier group, as the backend defines it. */
export function groupRule(group: ModifierGroup): string {
  const { min_selection: min, max_selection: max } = group;
  if (min === 0) return `Optional · up to ${max}`;
  if (min === max) return `Required · choose ${min}`;
  return `Required · choose ${min} to ${max}`;
}

export type ManageAction = 'load' | 'availability';

/** Plain words for a failed management action. Never a code, a raw message or a stack trace. */
export function describeManageError(error: unknown, action: ManageAction): string {
  if (!isApiError(error)) return 'Something went wrong. Please try again.';
  if (error.isNetworkError) {
    return action === 'load'
      ? "Couldn't reach the server. Check the connection and try again."
      : "Couldn't confirm whether the change was saved. Check the item's status before trying again.";
  }
  if (error.status === 401) return 'Your session has ended. Please sign in again.';
  if (error.status === 403) return "You're not allowed to do that.";
  if (error.hasCode('MENU_ITEM_NOT_FOUND') || error.status === 404)
    return 'That item could not be found. It may have been removed. The menu has been refreshed.';
  if (error.status === 400 || error.status === 422)
    return "That change wasn't accepted. The menu has been refreshed.";
  if (error.status === 409)
    return 'That changed while you were working. The menu has been refreshed.';
  if (error.status >= 500) return 'Something went wrong on our side. Try again in a moment.';
  return "That didn't go through. Please try again.";
}

/** "5 min ago", from the request's own timestamp and the current time. */
export function waitedText(createdAt: string, now: number): string {
  const minutes = Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 60_000));
  return minutes < 1 ? 'Just now' : `${formatDuration(minutes)} ago`;
}
